import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import { Avatar, Box, Fab, Grid, Stack, Typography } from "@mui/material";
import { t } from "i18next";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import io from "socket.io-client";
import GlobalButton from "../../../components/button";
import CustomContainer from "../../../components/container/CustomContainer";
import DashboardHeader from "../../../components/dashboardHeader";
import ReviewDialog from "../../../components/reviewDialog";
import SkeletonLoader from "../../../components/skeletons/ServiceDetailSkeleton";
import { useSnackbar } from "../../../lib/context/snackbarContext";
import { CURRENCY } from "../../../lib/variables";
import { socketUrl } from "../../../services";
import {
  cancelAppointmentById,
  getAppointmentDetail,
} from "../../../services/appointmentFn";
import CancelALert from "./../../../components/alert/CancelAlert";
import AppointmentChatDialog from "./ChatDialog";
import { useSelector } from "react-redux";
import InvoiceViewer from "./InvoiceViewer";

const socket = io.connect(socketUrl);

const LocalContainer = ({ children, ...props }) => {
  return (
    <Stack
      p={{ xs: 1, md: 2 }}
      border={"1px solid"}
      borderColor={"text.borderGrey"}
      borderRadius={1}
      {...props}
    >
      {children}
    </Stack>
  );
};

const DetailRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" py={0.5}>
    <Typography variant="body1" fontWeight={500} textTransform={"capitalize"}>
      {t(label)}:
    </Typography>
    <Typography
      variant="body1"
      color="text.secondary"
      textTransform={"capitalize"}
    >
      {t(value) || "N/A"}
    </Typography>
  </Stack>
);

const ServiceDetailPage = () => {
  const navigate = useNavigate();
  const openReviewDialog = useRef();
  const openCancelAlert = useRef();
  const openChatDialog = useRef(null);
  const { appointmentId } = useParams();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [appointmentDetail, setAppointmentDetail] = useState(null);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  useEffect(() => {
    if (appointmentId) {
      fetchAppointmentDetail();
    }
  }, [appointmentId]);

  const fetchAppointmentDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getAppointmentDetail(appointmentId);
      if (response?.data.status === "success") {
        setAppointmentDetail(response.data.data);
      } else {
        showSnackbar(response?.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setIsLoading(true);
      const response = await cancelAppointmentById(appointmentDetail?._id);

      if (response?.data?.status === "success") {
        openCancelAlert?.current?.closeDialog();
        fetchAppointmentDetail();
        showSnackbar(response?.data?.message, "success");
      } else {
        showSnackbar(response?.data?.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeadProps = () => {
    return (
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={"center"}
        spacing={2}
      >
        <GlobalButton
          handleClickBtn={() =>
            openReviewDialog.current.openDialog({
              type: "service",
              getData: fetchAppointmentDetail,
              workerId: appointmentDetail?.workerId?._id,
              appointmentId: appointmentDetail?._id,
            })
          }
          disabled={appointmentDetail?.isReviewed}
          btnText={
            appointmentDetail?.isReviewed ? t("reviewAdded") : t("addReview")
          }
          styleProps={{
            width: "auto",
          }}
        />
      </Stack>
    );
  };
  const renderCancelAppointmentBtn = () => {
    return (
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={"center"}
        spacing={2}
      >
        <GlobalButton
          handleClickBtn={() =>
            openCancelAlert.current.openDialog({
              warningText: t("cancelAppointmentMsg"),
              data: appointmentDetail,
            })
          }
          disabled={appointmentDetail?.isReviewed}
          btnText={"cancelAppointment"}
          styleProps={{
            width: "auto",
            background: "red",
          }}
        />
      </Stack>
    );
  };

  if (isLoading || !appointmentDetail) {
    return (
      <DashboardHeader>
        <CustomContainer
          primaryHeading={t("serviceDetails")}
          secondaryHeading={t("trackServiceInfo")}
        >
          <SkeletonLoader />
        </CustomContainer>
      </DashboardHeader>
    );
  }

  const {
    status,
    start,
    end,
    totalCost,
    userInputs,
    mainServiceDetails,
    serviceSubCategoryDetails,
    companyDetails,
    userId,
    workerDetails,
    clientAnswers,
    location,
    isReviewed,
    platformFee,
    platformPercentage,
  } = appointmentDetail;

  return (
    <DashboardHeader>
      {/* <AppLoader isVisible={isLoading} /> */}
      <CustomContainer
        primaryHeading={t("serviceDetails")}
        secondaryHeading={t("trackServiceInfo")}
        showBackButton
        handleBack={() => navigate("/dashboard/service")}
        headProps={
          
          status === "completed"
            ? renderHeadProps()
            : status == "pending"
            ? renderCancelAppointmentBtn()
            : null
        }
      >
        <Grid container spacing={2}>
          {/* Appointment Details */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {t("appointmentDetails")}
            </Typography>
            <LocalContainer>
              <DetailRow
                label="appointmentId"
                value={`#${appointmentDetail?.appointmentId}`}
              />
              <DetailRow label="status" value={status} />
              <DetailRow
                label="startTime"
                value={moment(start).format("MM-DD-YYYY hh:mm A")}
              />
              <DetailRow
                label="endTime"
                value={moment(end).format("MM-DD-YYYY hh:mm A")}
              />
              <DetailRow
                label="serviceCharges"
                value={`${CURRENCY} ${totalCost - platformFee}`}
              />
              {/* <DetailRow
                label="Platform Percentage"
                value={`${platformPercentage}%`}
              /> */}
              <DetailRow
                label="platformCharges"
                value={`${CURRENCY} ${platformFee}`}
              />
              <DetailRow label="totalCost" value={`${CURRENCY} ${totalCost}`} />
              {userInputs?.selectedType === "room+area" &&
                userInputs?.selectedOption === "room" && (
                  <DetailRow
                    label="userInput"
                    value={`${userInputs?.userInput} Room`}
                  />
                )}
              {userInputs?.selectedType === "room+area" &&
                userInputs?.selectedOption === "area" && (
                  <DetailRow
                    label="userInput"
                    value={`${userInputs?.userInput} sqft`}
                  />
                )}

              {userInputs.selectedType === "room+area" &&
                userInputs.selectedOption === "apartment" && (
                  <DetailRow
                    label="userInput"
                    value={`${userInputs?.userInput} sqft`}
                  />
                )}
              {userInputs.selectedType === "room+area" &&
                userInputs.selectedOption === "deluxe" && (
                  <DetailRow
                    label="userInput"
                    value={`${userInputs?.userInput} sqft`}
                  />
                )}
              {userInputs.selectedType === "area" &&
                userInputs.selectedOption === "area" && (
                  <DetailRow
                    label="userInput"
                    value={`${userInputs?.userInput} sqft`}
                  />
                )}
              {userInputs.selectedType === "fixed" &&
                userInputs.selectedOption === "fixed" && (
                  <DetailRow label="userInput" value={`car`} />
                )}
              {userInputs.selectedType === "interior+exterior" &&
                userInputs.selectedOption === "interior" && (
                  <DetailRow label="userInput" value={`Interior`} />
                )}
              {userInputs.selectedType === "interior+exterior" &&
                userInputs.selectedOption === "exterior" && (
                  <DetailRow label="userInput" value={`Exerior`} />
                )}
              {userInputs.selectedType === "interior+exterior" &&
                userInputs.selectedOption === "both" && (
                  <DetailRow label="userInput" value={`Interior + Exterior`} />
                )}
              {userInputs?.cleaningType && (
                <DetailRow
                  label="cleaningType"
                  value={userInputs?.cleaningType}
                />
              )}
            </LocalContainer>
          </Grid>

          {/* Service Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600}>
              {t("serviceDetails")}
            </Typography>
            <LocalContainer>
              <DetailRow
                label="mainService"
                value={mainServiceDetails?.name[currentLanguage]}
              />
              <DetailRow
                label="subService"
                value={serviceSubCategoryDetails?.name[currentLanguage]}
              />
              <Typography mt={2}>
                {serviceSubCategoryDetails?.description[currentLanguage]}
              </Typography>
            </LocalContainer>
          </Grid>

          {/* Company Details */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {t("companyDetails")}
            </Typography>
            <LocalContainer>
              <Stack direction="row" alignItems="center" gap={2}>
                <Avatar src={companyDetails?.profileImg} alt="Company Logo" />
                <Typography variant="h6">{companyDetails?.name}</Typography>
              </Stack>
              <DetailRow label="email" value={companyDetails?.email} />
              <DetailRow label="contact" value={companyDetails?.contact} />
              <DetailRow
                label="location"
                value={companyDetails?.location?.addressLabel}
              />
            </LocalContainer>
          </Grid>

          {/* Worker Details */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {t("workerDetails")}
            </Typography>
            <LocalContainer>
              <Stack direction="row" alignItems="center" gap={2}>
                <Avatar src={workerDetails?.profileImg} alt="Worker Photo" />
                <Typography variant="h6">{workerDetails?.name}</Typography>
              </Stack>
              <DetailRow label="email" value={workerDetails?.email} />
              <DetailRow label="contact" value={workerDetails?.contact} />
              <DetailRow label="expertise" value={workerDetails?.expertise} />
            </LocalContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600}>
              {t("address")}
            </Typography>
            <LocalContainer>
              <DetailRow
                label="userName"
                value={`${location?.firstname} ${location?.lastname}`}
              />
              <DetailRow label="email" value={location?.email} />
              <DetailRow label="contact" value={location?.contact} />
              <DetailRow label="country" value={location?.country} />
              <DetailRow label="state" value={location?.state} />
              <DetailRow label="city" value={location?.city} />
              <DetailRow label="zipCode" value={location?.zipCode} />
              <DetailRow label="address" value={location?.address} />
            </LocalContainer>
          </Grid>
          {clientAnswers?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                fontWeight={600}
                textTransform={"capitalize"}
              >
                {t("clientAnswer")}
              </Typography>
              <LocalContainer>
                <Stack gap={0.4}>
                  {clientAnswers?.map((val, index) => {
                    return (
                      <Box key={index}>
                        <Typography>
                          Q. {val.question[currentLanguage]}
                        </Typography>
                        <Typography color="text.slateGray">
                          A. {val.answer}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </LocalContainer>
            </Grid>
          )}

        </Grid>
      <InvoiceViewer invoiceData={appointmentDetail} />
      </CustomContainer>
      <ReviewDialog ref={openReviewDialog} />
      <AppointmentChatDialog ref={openChatDialog} socket={socket} />

      <Fab
        disabled={status === "completed" ? true : false}
        onClick={() => openChatDialog.current.openDialog(appointmentDetail)}
        color="primary"
        aria-label="chat"
        sx={{
          position: "fixed",
          right: "15px",
          bottom: "15px",
        }}
      >
        <ChatOutlinedIcon htmlColor="text.white" />
      </Fab>
      <CancelALert ref={openCancelAlert} onCancel={handleCancel} />

    </DashboardHeader>
  );
};

export default ServiceDetailPage;
